import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Users API
export const usersApi = {
  getAll: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  contractorUpdateWorker: (id, data) => api.put(`/contractor/workers/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getDependencies: (id) => api.get(`/users/${id}/dependencies`),
  bulkDependencies: (userIds) => api.post('/users/bulk-dependencies', { user_ids: userIds }),
  bulkDelete: (userIds) => api.post('/users/bulk-delete', { user_ids: userIds }),
  bulkCreate: (data) => api.post('/users/bulk', data),
  bulkUpload: (data) => api.post('/users/bulk-upload', data),
  bulkUploadUpdate: (data) => api.post('/users/bulk-upload-update', data),
  checkDuplicate: (data) => api.post('/users/check-duplicate', data),
  checkBulkDuplicates: (data) => api.post('/users/check-bulk-duplicates', data),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
};

// Companies API
export const companiesApi = {
  getAll: (params) => api.get('/companies', { params }),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  deleteCascade: (id) => api.delete(`/companies/${id}/cascade`),
  getDependencies: (id) => api.get(`/companies/${id}/dependencies`),
  bulkDependencies: (companyIds) => api.post('/companies/bulk-dependencies', { company_ids: companyIds }),
  bulkDelete: (companyIds) => api.post('/companies/bulk-delete', { company_ids: companyIds }),
  bulkUpload: (data) => api.post('/companies/bulk-upload', data),
  getAssignedUsers: (id) => api.get(`/companies/${id}/assigned-users`),
  getAvailableUsers: (id, search) => api.get(`/companies/${id}/available-users`, { params: { search } }),
  assignUser: (id, userId) => api.post(`/companies/${id}/assign-user`, { user_id: userId }),
  unassignUser: (companyId, userId) => api.delete(`/companies/${companyId}/unassign-user/${userId}`),
  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/companies/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: (id) => api.delete(`/companies/${id}/logo`),
  logoUrl: (id) => `${API_URL}/companies/${id}/logo`,
};

export const notificationsApi = {
  massPreview: (clientId, day) => api.get('/notifications/mass/preview', { params: { client_id: clientId, day } }),
  massSend: (clientId, day, cc) => api.post('/notifications/mass/send', { client_id: clientId, day, cc }),
  massPreviewEmail: (clientId, day, workerId) => api.get('/notifications/mass/preview-email', { params: { client_id: clientId, day, worker_id: workerId } }),
  massExport: (clientId, day) => api.get('/notifications/mass/export', { params: { client_id: clientId, day }, responseType: 'blob' }),
};


// Courses API
export const coursesApi = {
  getAll: (params) => api.get('/courses', { params }),
  getById: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  setTestSchedule: (id, start, end) => api.put(`/courses/${id}/test-schedule`, { start, end }),
  getMaterials: (id) => api.get(`/courses/${id}/materials`),
  addMaterial: (id, data) => api.post(`/courses/${id}/materials`, data),
  deleteMaterial: (courseId, materialId) => api.delete(`/courses/${courseId}/materials/${materialId}`),
  getEvaluations: (id) => api.get(`/courses/${id}/evaluations`),
  createEvaluation: (id, data) => api.post(`/courses/${id}/evaluations`, data),
  updateEvaluation: (courseId, evalId, data) => api.put(`/courses/${courseId}/evaluations/${evalId}`, data),
  deleteEvaluation: (courseId, evalId) => api.delete(`/courses/${courseId}/evaluations/${evalId}`),
  getEvalQuestions: (courseId, evalId) => api.get(`/courses/${courseId}/evaluations/${evalId}/questions`),
  addEvalQuestion: (courseId, evalId, questionId) => api.post(`/courses/${courseId}/evaluations/${evalId}/questions`, { question_id: questionId }),
  removeEvalQuestion: (courseId, evalId, questionId) => api.delete(`/courses/${courseId}/evaluations/${evalId}/questions/${questionId}`),
  delete: (id) => api.delete(`/courses/${id}`),
  getDependencies: (id) => api.get(`/courses/${id}/dependencies`),
  bulkDependencies: (courseIds) => api.post('/courses/bulk-dependencies', { course_ids: courseIds }),
  bulkDelete: (courseIds) => api.post('/courses/bulk-delete', { course_ids: courseIds }),
};

// Trainers API
export const trainersApi = {
  getAll: () => api.get('/trainers'),
  create: (data) => api.post('/trainers', data),
  update: (id, data) => api.put(`/trainers/${id}`, data),
};

// Trainings API
export const trainingsApi = {
  getAll: (params) => api.get('/trainings', { params }),
  getById: (id) => api.get(`/trainings/${id}`),
  create: (data) => api.post('/trainings', data),
  update: (id, data) => api.put(`/trainings/${id}`, data),
  conformar: (id, sendEmails, cc) => api.post(`/trainings/${id}/conformar`, { send_emails: sendEmails, cc }),
  desconformar: (id) => api.post(`/trainings/${id}/desconformar`),
  realizada: (id) => api.post(`/trainings/${id}/realizada`),
  sendInvitations: (id, cc) => api.post(`/trainings/${id}/send-invitations`, { cc }),
  sendInvitation: (id, workerId, cc) => api.post(`/trainings/${id}/send-invitation/${workerId}`, { cc }),
  invitationPreview: (id, workerId) => api.get(`/trainings/${id}/invitation-preview`, { params: workerId ? { worker_id: workerId } : {} }),
  setTestActivation: (id, data) => api.put(`/trainings/${id}/test-activation`, data),
  getAvailableWorkers: (id, search, companyId) => api.get(`/trainings/${id}/available-workers`, { params: { search, company_id: companyId } }),
  checkEligibility: (id, workerIds) => api.post(`/trainings/${id}/check-eligibility`, { worker_ids: workerIds }),
  enrollWorker: (id, workerId) => api.post(`/trainings/${id}/enroll-worker`, { worker_id: workerId }),
  enrollWorkersBulk: (id, workerIds) => api.post(`/trainings/${id}/enroll-workers-bulk`, { worker_ids: workerIds }),
  autoAssign: (id) => api.post(`/trainings/${id}/auto-assign`),
  delete: (id) => api.delete(`/trainings/${id}`),
  getDependencies: (id) => api.get(`/trainings/${id}/dependencies`),
  bulkDependencies: (trainingIds) => api.post('/trainings/bulk-dependencies', { training_ids: trainingIds }),
  bulkDelete: (trainingIds) => api.post('/trainings/bulk-delete', { training_ids: trainingIds }),
};

// Enrollments API
export const enrollmentsApi = {
  getAll: (params) => api.get('/enrollments', { params }),
  create: (data) => api.post('/enrollments', data),
  bulkCreate: (data) => api.post('/enrollments/bulk', data),
  markAttendance: (id, data) => api.put(`/enrollments/${id}/attendance`, data || {}),
  evaluate: (id, data) => api.put(`/enrollments/${id}/evaluate`, data),
  delete: (id) => api.delete(`/enrollments/${id}`),
  approve: (id) => api.put(`/enrollments/${id}/approve`),
  reject: (id) => api.put(`/enrollments/${id}/reject`),
};

// Course Requests (Solicitudes) API
export const courseRequestsApi = {
  getAll: (params) => api.get('/course-requests', { params }),
  create: (data) => api.post('/course-requests', data),
  getAvailableCourses: () => api.get('/contractor/available-courses'),
  getEapCourses: () => api.get('/contractor/eap-courses'),
  approveToTraining: (requestId, trainingId) => api.post(`/course-requests/${requestId}/approve-to-training/${trainingId}`),
  reject: (requestId) => api.post(`/course-requests/${requestId}/reject`),
};


// Questions API
export const questionsApi = {
  getAll: (params) => api.get('/questions', { params }),
  getGroups: (params) => api.get('/questions/groups', { params }),
  create: (data) => api.post('/questions', data),
  update: (id, data) => api.put(`/questions/${id}`, data),
  delete: (id) => api.delete(`/questions/${id}`),
  getDependencies: (id) => api.get(`/questions/${id}/dependencies`),
  bulkDelete: (ids) => api.post('/questions/bulk-delete', { ids }),
  bulkDependencies: (ids) => api.post('/questions/bulk-dependencies', { ids }),
};

// Test Bank API
export const testBankApi = {
  getAll: (params) => api.get('/test-bank', { params }),
  getById: (id) => api.get(`/test-bank/${id}`),
  create: (data) => api.post('/test-bank', data),
  update: (id, data) => api.put(`/test-bank/${id}`, data),
  delete: (id) => api.delete(`/test-bank/${id}`),
  getQuestions: (id) => api.get(`/test-bank/${id}/questions`),
  getAvailableQuestions: (id) => api.get(`/test-bank/${id}/available-questions`),
  getDependencies: (id) => api.get(`/test-bank/${id}/dependencies`),
  addQuestion: (id, questionId) => api.post(`/test-bank/${id}/questions`, { question_id: questionId }),
  removeQuestion: (id, questionId) => api.delete(`/test-bank/${id}/questions/${questionId}`),
};

// Course-Test Assignment API
export const courseTestsApi = {
  getAssignedTests: (courseId) => api.get(`/courses/${courseId}/assigned-tests`),
  assignTest: (courseId, testBankId) => api.post(`/courses/${courseId}/assign-test`, { test_bank_id: testBankId }),
  unassignTest: (courseId, testBankId) => api.delete(`/courses/${courseId}/unassign-test/${testBankId}`),
};

// Evaluations API
export const evaluationsApi = {
  getByTraining: (trainingId) => api.get(`/evaluations/training/${trainingId}`),
  start: (evaluationId) => api.post('/evaluations/start', { evaluation_id: evaluationId }),
  submit: (data) => api.post('/evaluations/submit', data),
  getMyAttempts: () => api.get('/evaluations/attempts/me'),
};

// Certificates API
export const certificatesApi = {
  getAll: (params) => api.get('/certificates', { params }),
  download: (id) => api.get(`/certificates/${id}/download`, { responseType: 'blob' }),
  preview: (id) => api.get(`/certificates/${id}/preview`, { responseType: 'blob' }),
  generate: (data) => api.post('/certificates/generate', data),
  validate: (code) => api.get(`/certificates/validate/${code}`),
  consolidatedByCompany: (params) => api.get('/certificates/consolidated/by-company', { params, responseType: 'blob' }),
  consolidatedByDay: (params) => api.get('/certificates/consolidated/by-day', { params, responseType: 'blob' }),
  consolidatedCompaniesForDay: (params) => api.get('/certificates/consolidated/companies-for-day', { params }),
  myCompanyConsolidatedDays: () => api.get('/certificates/consolidated/my-company/days'),
  myCompanyConsolidated: (params) => api.get('/certificates/consolidated/my-company', { params, responseType: 'blob' }),
};

// Reports API (Admin only)
export const reportsApi = {
  workers: (params) => api.get('/reports/workers', { params }),
  companies: (params) => api.get('/reports/companies', { params }),
  exportWorkers: (params) => api.get('/reports/workers/export', { params, responseType: 'blob' }),
  exportCompanies: (params) => api.get('/reports/companies/export', { params, responseType: 'blob' }),
  mandantes: () => api.get('/reports/mandantes'),
  mandante: (id, params) => api.get(`/reports/mandante/${id}`, { params }),
  exportMandante: (id, params) => api.get(`/reports/mandante/${id}/export`, { params, responseType: 'blob' }),
};

// Dashboard API
export const dashboardApi = {
  admin: () => api.get('/dashboard/admin'),
  contractor: () => api.get('/dashboard/contractor'),
  worker: () => api.get('/dashboard/worker'),
  client: () => api.get('/dashboard/client'),
};

export const settingsApi = {
  getEnrollmentRules: () => api.get('/settings/enrollment-rules'),
  updateEnrollmentRules: (data) => api.put('/settings/enrollment-rules', data),
};

export const lifeSavingApi = {
  create: (data) => api.post('/life-saving-violations', data),
  list: (params) => api.get('/life-saving-violations', { params }),
};

export const documentsApi = {
  list: () => api.get('/documents'),
  upload: (formData) => api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  download: (id) => `${BACKEND_URL}/api/documents/${id}/download`,
  delete: (id) => api.delete(`/documents/${id}`),
};

export const instrumentsApi = {
  list: (courseId) => api.get('/instruments', { params: courseId ? { course_id: courseId } : {} }),
  create: (data) => api.post('/instruments', data),
  update: (id, data) => api.put(`/instruments/${id}`, data),
  delete: (id) => api.delete(`/instruments/${id}`),
};

export const relatorApi = {
  getTrainings: () => api.get('/relator/trainings'),
  getTrainingDetail: (id) => api.get(`/relator/trainings/${id}`),
  markAttendance: (id, data) => api.post(`/relator/trainings/${id}/attendance`, data),
  evaluate: (id, data) => api.post(`/relator/trainings/${id}/evaluate`, data),
  closeTraining: (id) => api.post(`/relator/trainings/${id}/close`),
  getRelators: () => api.get('/relators'),
};

export const messagesApi = {
  getInbox: () => api.get('/messages/inbox'),
  getSent: () => api.get('/messages/sent'),
  getImportant: () => api.get('/messages/important'),
  getMessage: (id) => api.get(`/messages/${id}`),
  getThread: (id) => api.get(`/messages/${id}/thread`),
  send: (data) => api.post('/messages', data),
  getUnreadCount: () => api.get('/messages/unread-count'),
  getContacts: () => api.get('/messages/contacts'),
  toggleImportant: (id) => api.put(`/messages/${id}/important`),
  deleteMessage: (id) => api.delete(`/messages/${id}`),
  bulkDelete: (data) => api.post('/messages/bulk-delete', data),
};

export default api;
